import { FC, useState, useEffect, useCallback } from "react";
import {
  Box,
  List,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSearchParams } from "react-router-dom";
import { InternalEmailThreadComponent } from "../../internal/Email/InternalEmailThreadComponent";
import { EmailListItem } from "../../internal/Email/EmailListItemComponent";
import { downloadMessagesAsCSV } from "./csvDownloadHelpers";
import { EmailThreadComponent } from "../../components/EmailThreadComponent";
import {
  EmailMessage,
  EmailSearchMessagesResponse,
  EmailSearchMessagesBody,
  EmailSearchMultipleInboxesResponse,
  EmailMessageWithInbox,
  ConnectedInboxData,
} from "@services/documents/email/types";
import {
  useEmailSelection,
  useBatchEmailActions,
  BatchActionBar,
  TestSetSelectorDialog,
  WorkflowEntityDialog,
  type EmailReference,
} from "./emailActions";

interface UserInfo {
  id: string;
  email: string;
  createdAt: Date;
  hasConnectedEmail: boolean;
  organizationName?: string;
}

export const SearchMessagesInternalPage: FC = () => {
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [selectedMessage, setSelectedMessage] = useState<EmailMessage | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("query") || "",
  );
  const [messageIdQuery, setMessageIdQuery] = useState(
    searchParams.get("messageId") || "",
  );
  const [headerMessageIdQuery, setHeaderMessageIdQuery] = useState(
    searchParams.get("hmid") || searchParams.get("messageHeaderId") || "",
  );
  const [emailIdQuery, setEmailIdQuery] = useState(
    searchParams.get("emailid") || searchParams.get("emailId") || "",
  );
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  // Multi-inbox search state
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [selectedInboxes, setSelectedInboxes] = useState<UserInfo[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [returnAllThreadMessages, setReturnAllThreadMessages] = useState(false);
  const [searchEachWordSeparately, setSearchEachWordSeparately] =
    useState(false);
  const [maxMessagesPerWordSearch, setMaxMessagesPerWordSearch] = useState(100);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sharedInbox, setSharedInbox] = useState<string>(
    searchParams.get("sharedInbox") || "",
  );
  const [includeDeletedItems, setIncludeDeletedItems] = useState(false);

  // Email selection state for batch actions
  const selection = useEmailSelection();
  const batchActions = useBatchEmailActions({ selection });

  const handleMessageSelect = (message: EmailMessage) => {
    setSelectedMessage(message);
  };

  // Handle clicking on inbox chips to open thread from specific user
  // Handle copying link with messageHeaderId
  const handleCopyLink = useCallback(
    (message: EmailMessage) => {
      const url = new URL(window.location.href);
      if (message.headerMessageId) {
        url.searchParams.set("messageHeaderId", message.headerMessageId);
        url.searchParams.delete("messageId");
      } else if (message.messageId) {
        url.searchParams.set("messageId", message.messageId);
        url.searchParams.delete("messageHeaderId");
      } else {
        console.error("cannot copy link");
      }

      // Include selected inboxes in the copied link
      if (selectedInboxes.length > 0) {
        url.searchParams.set(
          "inboxes",
          selectedInboxes.map((user) => user.email).join(","),
        );
      } else {
        url.searchParams.delete("inboxes");
      }

      // Include shared inbox if set
      if (sharedInbox.trim()) {
        url.searchParams.set("sharedInbox", sharedInbox.trim());
      } else {
        url.searchParams.delete("sharedInbox");
      }

      // Remove other search parameters to focus on this specific message
      url.searchParams.delete("hmid");
      url.searchParams.delete("query");

      navigator.clipboard
        .writeText(url.toString())
        .then(() => {
          // Could add a toast notification here if needed
          console.log("Link copied to clipboard");
        })
        .catch((err) => {
          console.error("Failed to copy link:", err);
        });
    },
    [selectedInboxes, sharedInbox],
  );

  const handleInboxChipClick = (inboxData: ConnectedInboxData) => {
    // Find the message that contains this inbox data
    const messageWithInbox = messages.find((msg) =>
      (msg as EmailMessageWithInbox).connectedInboxesData?.some(
        (data) =>
          data.inboxEmail === inboxData.inboxEmail &&
          data.messageId === inboxData.messageId,
      ),
    );

    if (messageWithInbox) {
      setSelectedMessage({
        ...messageWithInbox,
        // Override with specific inbox data for this user
        inboxEmail: inboxData.inboxEmail,
        messageId: inboxData.messageId,
        threadId: inboxData.threadId,
        headerMessageId: inboxData.headerMessageId,
      } as EmailMessage);
    }
  };

  // Fetch users for inbox selection
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await apiClient.post<{ users: UserInfo[] }>(
        "/admin/list_users",
        {},
      );
      // Filter to only show users with connected email
      const usersWithEmail = response.users.filter(
        (user) => user.hasConnectedEmail,
      );
      setUsers(usersWithEmail);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Multi-inbox search function
  const fetchMultipleInboxMessages = useCallback(
    async (
      query: string,
      messageId?: string,
      headerMessageId?: string,
      autoSelectFirst?: boolean,
    ) => {
      try {
        setError(null);
        setSearching(true);

        // If a specific message ID or header message ID is provided,
        // force returnAllThreadMessages to false regardless of user setting
        const shouldReturnAllThreadMessages =
          messageId || headerMessageId ? false : returnAllThreadMessages;

        const searchBody = {
          query: messageId || headerMessageId ? "" : query,
          messageId: messageId || undefined,
          headerMessageId: headerMessageId || undefined,
          emails: selectedInboxes.map((user) => user.email),
          returnAllThreadMessages: shouldReturnAllThreadMessages,
          searchEachWordSeparately: searchEachWordSeparately,
          maxMessagesPerWordSearch: maxMessagesPerWordSearch,
          startDate: startDate
            ? startDate.toISOString().split("T")[0]
            : undefined,
          endDate: endDate ? endDate.toISOString().split("T")[0] : undefined,
          sharedInbox: sharedInbox.trim() || undefined,
          includeDeletedItems: includeDeletedItems,
        };
        const response =
          await apiClient.post<EmailSearchMultipleInboxesResponse>(
            "/documents/email/search_messages_in_multiple_inboxes",
            searchBody,
          );
        setMessages(response.messages);
        setNextPageToken(undefined); // Multi-inbox search doesn't support pagination

        // Auto-select first message when searching by direct ID
        if (autoSelectFirst && response.messages.length > 0) {
          setSelectedMessage(response.messages[0]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to search messages",
        );
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [
      selectedInboxes,
      returnAllThreadMessages,
      searchEachWordSeparately,
      maxMessagesPerWordSearch,
      startDate,
      endDate,
      sharedInbox,
      includeDeletedItems,
    ],
  );

  const fetchMessages = useCallback(
    async (
      query: string,
      messageId?: string,
      headerMessageId?: string,
      autoSelectFirst?: boolean,
    ) => {
      try {
        setError(null);
        setSearching(true);
        const searchBody: EmailSearchMessagesBody = {
          query: messageId || headerMessageId ? "" : query,
          messageId: messageId || undefined,
          headerMessageId: headerMessageId || undefined,
          tags: [],
          limit: 10,
          offset: 0,
          pageToken: undefined,
        };
        const response = await apiClient.post<EmailSearchMessagesResponse>(
          "/documents/email/search_messages",
          searchBody,
        );
        setMessages(response.messages);
        setNextPageToken(response.nextPageToken);

        // Auto-select first message when searching by direct ID
        if (autoSelectFirst && response.messages.length > 0) {
          setSelectedMessage(response.messages[0]);
        }
      } catch (err) {
        setError(
          "Failed to load messages. If you just changed user, check the url and remove ?messageId=",
        );
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    // Fetch users for inbox selection
    fetchUsers();
  }, []);

  // Initialize selected inboxes from URL after users are loaded
  useEffect(() => {
    if (users.length > 0) {
      const inboxesParam = searchParams.get("inboxes");
      if (inboxesParam) {
        const inboxEmails = inboxesParam.split(",");
        const selectedUsers = users.filter((user) =>
          inboxEmails.includes(user.email),
        );
        setSelectedInboxes(selectedUsers);
      }
    }
  }, [users, searchParams]);

  // Handle emailId URL parameter to auto-open popup
  useEffect(() => {
    const emailIdParam =
      searchParams.get("emailid") || searchParams.get("emailId");
    if (emailIdParam) {
      setEmailIdQuery(emailIdParam);
      setEmailPopupOpen(true);
    }
  }, [searchParams]);

  // Initial search after users and inbox selection are set - only runs on page load with URL parameters
  useEffect(() => {
    if (users.length > 0) {
      const messageId = searchParams.get("messageId");
      // Support both hmid and messageHeaderId URL params (normalize to headerMessageId)
      const headerMessageId =
        searchParams.get("hmid") || searchParams.get("messageHeaderId");
      const queryParam = searchParams.get("query");
      const inboxesParam = searchParams.get("inboxes");

      // If there's an inboxes parameter, wait for selectedInboxes to be populated
      if (inboxesParam && selectedInboxes.length === 0) {
        return; // Wait for selectedInboxes to be set from URL
      }

      // Determine if we should use multi-inbox search based on URL parameter AND state
      const shouldUseMultiInbox = inboxesParam && selectedInboxes.length > 0;

      // Auto-select first result when searching by direct ID
      const isDirectIdSearch = !!(messageId || headerMessageId);

      if (messageId) {
        if (shouldUseMultiInbox) {
          fetchMultipleInboxMessages(
            "",
            messageId,
            undefined,
            isDirectIdSearch,
          );
        } else {
          fetchMessages("", messageId, undefined, isDirectIdSearch);
        }
      } else if (headerMessageId) {
        if (shouldUseMultiInbox) {
          fetchMultipleInboxMessages(
            "",
            undefined,
            headerMessageId,
            isDirectIdSearch,
          );
        } else {
          fetchMessages("", undefined, headerMessageId, isDirectIdSearch);
        }
      } else if (queryParam) {
        // Only auto-search if there's a query parameter in the URL
        if (shouldUseMultiInbox) {
          fetchMultipleInboxMessages(queryParam);
        } else {
          fetchMessages(queryParam);
        }
      }
    }
  }, [
    users,
    selectedInboxes,
    searchParams,
    fetchMultipleInboxMessages,
    fetchMessages,
  ]);

  const handleSearch = () => {
    setMessages([]);
    setNextPageToken(undefined);

    // Use multi-inbox search if inboxes are selected, otherwise use regular search
    if (selectedInboxes.length > 0) {
      fetchMultipleInboxMessages(
        searchQuery,
        messageIdQuery,
        headerMessageIdQuery,
      );
    } else {
      fetchMessages(searchQuery, messageIdQuery, headerMessageIdQuery);
    }
  };

  const handleLoadMore = async () => {
    if (!nextPageToken || loading) return;
    try {
      setLoading(true);
      const searchBody: EmailSearchMessagesBody = {
        query: messageIdQuery || headerMessageIdQuery ? "" : searchQuery,
        messageId: messageIdQuery || undefined,
        headerMessageId: headerMessageIdQuery || undefined,
        tags: [],
        limit: 10,
        offset: 0,
        pageToken: nextPageToken,
      };
      const response = await apiClient.post<EmailSearchMessagesResponse>(
        "/documents/email/search_messages",
        searchBody,
      );
      setMessages((prev) => [...prev, ...response.messages]);
      setNextPageToken(response.nextPageToken);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more messages",
      );
    } finally {
      setLoading(false);
    }
  };

  // Remove the blocking error check - we'll show errors in the search results instead

  return (
    <AdminPage
      containerStyle={{
        height: "calc(100vh - 100px)",
        pt: "20px",
        marginTop: 0,
        boxSizing: "content-box",
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {/* Multi-Inbox Selection */}
        <Paper
          sx={{
            p: 0.5,
            pl: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {/* First row - Inbox selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <FormControl
              sx={{ minWidth: 300, maxWidth: 520, flexGrow: 1 }}
              size="small"
            >
              <InputLabel>Search in Inboxes (optional)</InputLabel>
              <Select
                multiple
                value={selectedInboxes.map((u) => u.email)}
                onChange={(event) => {
                  const value = event.target.value as string[];
                  const selectedUsers = users.filter((user) =>
                    value.includes(user.email),
                  );
                  setSelectedInboxes(selectedUsers);
                }}
                input={<OutlinedInput label="Search in Inboxes (optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((email) => (
                      <Chip key={email} label={email} size="small" />
                    ))}
                  </Box>
                )}
                disabled={usersLoading}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.email}>
                    <ListItemText
                      primary={user.email}
                      secondary={user.organizationName}
                    />
                    <Checkbox
                      checked={selectedInboxes.some(
                        (u) => u.email === user.email,
                      )}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {usersLoading && <CircularProgress size={20} />}
            <TextField
              variant="outlined"
              size="small"
              label="Shared Inbox (Microsoft only)"
              placeholder="e.g. shared@company.com"
              value={sharedInbox}
              onChange={(e) => setSharedInbox(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Box>

          {/* Second row - Date pickers and options - only show when multi-inbox search is active */}
          {selectedInboxes.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { minWidth: 140 },
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { minWidth: 140 },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="max results inbox/word"
                type="text"
                size="small"
                value={maxMessagesPerWordSearch}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 1 && value <= 1000) {
                    setMaxMessagesPerWordSearch(value);
                  }
                }}
                // slotProps={{
                //   htmlInput: { min: 1, max: 1000 },
                // }}
                sx={{
                  minWidth: 140,
                }}
              />

              {/* Checkboxes moved to the end */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={returnAllThreadMessages}
                    onChange={(e) =>
                      setReturnAllThreadMessages(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Return all messages from threads"
                sx={{
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={searchEachWordSeparately}
                    onChange={(e) =>
                      setSearchEachWordSeparately(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Search each word separately"
                sx={{
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDeletedItems}
                    onChange={(e) => setIncludeDeletedItems(e.target.checked)}
                    size="small"
                  />
                }
                label="Search in deleted items"
                sx={{
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Search Bar */}
        <Paper
          sx={{
            p: 0.5,
            pl: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Search Query"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              placeholder="Enter search query..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Search Query Examples:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}
                  >
                    Gmail Users:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", ml: 1 }}
                  >
                    • <code>in:sent</code> - Search sent emails
                    <br />• <code>in:inbox</code> - Search inbox
                    <br />• <code>in:trash</code> - Search deleted items
                    <br />• <code>from:user@example.com</code> - From specific
                    sender
                    <br />• <code>subject:meeting</code> - Subject contains
                    "meeting"
                    <br />• <code>has:attachment</code> - Has attachments
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}
                  >
                    Microsoft/Outlook Users:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", ml: 1 }}
                  >
                    • <code>folder:sent</code> - Search sent emails
                    <br />• <code>folder:inbox</code> - Search inbox
                    <br />• <code>folder:drafts</code> - Search drafts
                    <br />• <code>folder:trash</code> - Search deleted items
                    <br />• <code>from:user@example.com</code> - From specific
                    sender
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}
                  >
                    Text Search (Both):
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", ml: 1 }}
                  >
                    • <code>meeting notes</code> - Search email content
                    <br />• <code>folder:sent meeting</code> - Search "meeting"
                    in sent folder
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-start"
              sx={{
                "& .MuiTooltip-tooltip": {
                  maxWidth: 400,
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              <IconButton size="small" sx={{ color: "text.secondary" }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Message ID (mid)"
            placeholder="Search by message ID..."
            value={messageIdQuery}
            onChange={(e) => setMessageIdQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Header Message ID (hmid)"
            placeholder="Search by header message ID..."
            value={headerMessageIdQuery}
            onChange={(e) => setHeaderMessageIdQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button
            variant={"outlined"}
            onClick={handleSearch}
            disabled={searching}
            sx={{ pl: 1, pr: 1 }}
          >
            {searching ? "Searching..." : "Search"}
            {searching ? <CircularProgress size={24} /> : <SearchIcon />}
          </Button>
        </Paper>

        {/* Email ID Field */}
        <Paper
          sx={{
            p: 0.5,
            pl: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            label="Email ID"
            placeholder="Enter email ID to open email..."
            value={emailIdQuery}
            onChange={(e) => setEmailIdQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && emailIdQuery.trim()) {
                setEmailPopupOpen(true);
              }
            }}
            sx={{ width: 180 }}
          />
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              if (emailIdQuery.trim()) {
                setEmailPopupOpen(true);
              }
            }}
            disabled={!emailIdQuery.trim()}
            sx={{ textTransform: "none" }}
          >
            open email
          </Button>
        </Paper>

        <Box display="flex" flex={1} overflow="hidden">
          {/* Left Sidebar - Messages List */}
          <Paper
            sx={{
              width: 620,
              height: "100%",
              overflowY: "auto",
              borderRight: 1,
              borderColor: "divider",
            }}
            onScroll={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.scrollTop + target.clientHeight >=
                target.scrollHeight - 5
              ) {
                if (nextPageToken && !loading) {
                  handleLoadMore();
                }
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Messages ({messages.length})
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() =>
                  downloadMessagesAsCSV(messages, searchQuery, selectedInboxes)
                }
                disabled={messages.length === 0}
                sx={{ minWidth: "auto" }}
              >
                CSV
              </Button>
            </Box>
            {error && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "error.main",
                  color: "error.contrastText",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  ⚠️ Search Error
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}
            <List>
              {messages.map((message) => {
                // Get inbox email from message or connected inboxes
                const inboxEmail =
                  (message as any).inboxEmail ||
                  (message as EmailMessageWithInbox).connectedInboxesData?.[0]
                    ?.inboxEmail ||
                  selectedInboxes[0]?.email;

                return (
                  <Box key={message.messageId}>
                    <EmailListItem
                      id={message.messageId}
                      emailId={
                        message.id !== message.messageId ? message.id : null
                      }
                      sender={message.fromEmail}
                      threadId={message.threadId}
                      headerMessageId={message.headerMessageId}
                      subject={message.subject}
                      date={message.date.toString()}
                      snippet={message.snippet}
                      tags={message.tags}
                      connectedInboxesData={
                        (message as EmailMessageWithInbox).connectedInboxesData
                      }
                      isSelected={
                        selectedMessage?.messageId === message.messageId
                      }
                      onSelect={() => handleMessageSelect(message)}
                      onInboxChipClick={handleInboxChipClick}
                      onCopyLink={() => handleCopyLink(message)}
                      // Selection mode props
                      selectionMode={selectedInboxes.length > 0}
                      isChecked={selection.isSelected(message.messageId)}
                      onToggleCheck={() =>
                        selection.toggleSelect(message.messageId, {
                          emailId:
                            message.id && message.id !== message.messageId
                              ? message.id
                              : undefined,
                          messageId: message.messageId,
                          threadId: message.threadId,
                          inboxEmail: inboxEmail || undefined,
                          sharedInbox: sharedInbox.trim() || undefined,
                        })
                      }
                    />
                    <Divider />
                  </Box>
                );
              })}
            </List>
            {nextPageToken && (
              <Button
                sx={{ width: "100%", mt: 2 }}
                variant="contained"
                onClick={handleLoadMore}
                disabled={loading || searching}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Load More Messages"
                )}
              </Button>
            )}
          </Paper>

          {/* Right Column - Selected Email Thread */}
          <Box flex={1} height="100%" p={2} minWidth={0}>
            {selectedMessage && selectedMessage.threadId ? (
              <InternalEmailThreadComponent
                sx={{ maxWidth: "100%" }}
                threadId={selectedMessage.threadId}
                selectedMessageId={selectedMessage.messageId}
                showMessageIds={true}
                sharedInbox={sharedInbox.trim() || undefined}
                inboxEmail={
                  // Use the specific inbox email if user clicked on a chip, otherwise use first inbox
                  (selectedMessage as any).inboxEmail ||
                  (selectedMessage as EmailMessageWithInbox)
                    .connectedInboxesData?.[0]?.inboxEmail
                }
                // showMessageMetadata={true}
              />
            ) : (
              <Typography color="text.secondary">
                Select a message to view its email thread
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Email Thread Popup Modal */}
      {emailPopupOpen && emailIdQuery && (
        <Dialog
          open={emailPopupOpen}
          onClose={() => setEmailPopupOpen(false)}
          fullWidth
          maxWidth="lg"
          sx={{
            "& .MuiDialog-paper": {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Email ID: {emailIdQuery}</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setEmailPopupOpen(false)}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <EmailThreadComponent
              emailId={emailIdQuery}
              sx={{
                height: "100%",
                overflow: "auto",
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Batch Action Bar */}
      <BatchActionBar
        selectedCount={selection.selectedCount}
        totalCount={messages.length}
        onClose={selection.deselectAll}
        onAction={batchActions.handleActionClick}
        onSelectAll={() => {
          // Select all currently loaded messages
          const emailsToSelect = messages.map((message) => {
            const inboxEmail =
              (message as any).inboxEmail ||
              (message as EmailMessageWithInbox).connectedInboxesData?.[0]
                ?.inboxEmail ||
              selectedInboxes[0]?.email;
            return {
              messageId: message.messageId,
              ref: {
                emailId:
                  message.id && message.id !== message.messageId
                    ? message.id
                    : undefined,
                messageId: message.messageId,
                inboxEmail: inboxEmail || undefined,
                sharedInbox: sharedInbox.trim() || undefined,
              },
            };
          });
          selection.selectAll(emailsToSelect);
        }}
        onDeselectAll={selection.deselectAll}
        isExecuting={batchActions.isExecuting}
      />

      {/* Test Set Selector Dialog */}
      <TestSetSelectorDialog
        open={batchActions.testSetDialogOpen}
        onClose={() => batchActions.setTestSetDialogOpen(false)}
        onConfirm={batchActions.handleTestSetConfirm}
        isLoading={batchActions.isExecuting}
        selectedCount={selection.selectedCount}
      />

      {/* Workflow Entity Dialog */}
      <WorkflowEntityDialog
        open={batchActions.workflowEntityDialogOpen}
        onClose={() => batchActions.setWorkflowEntityDialogOpen(false)}
        onConfirm={batchActions.handleWorkflowEntityConfirm}
        isLoading={batchActions.isExecuting}
      />
    </AdminPage>
  );
};
